/**
 * Boîte à outils — points qu'on déplace au doigt, à la souris ou au clavier.
 *
 * Toucher la figure prend la poignée la plus proche (dans un rayon de
 * `grabRadius` px du viewBox, plus large que le point dessiné : cible de 44 px
 * sur téléphone) en gardant l'écart entre le doigt et le point, pour qu'il ne
 * saute pas. Loin de toute poignée, `onBackground` reçoit le point touché.
 * Au clavier, chaque poignée prend le focus et les flèches la déplacent de
 * `keyStep`. L'outil applique lui-même la grille et ses contraintes dans `onDrag`.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { pointerToViewBox, type Frame, type Pt } from './frame';

export interface DragOptions<K extends string> {
  svgRef: RefObject<SVGSVGElement | null>;
  frame: Frame;
  /** poignées présentes, la première gagne à égalité */
  ids: readonly K[];
  pointOf: (id: K) => Pt;
  onDrag: (id: K, p: Pt) => void;
  onEnd?: (id: K) => void;
  /** touché loin des poignées (ou glissé sans poignée) */
  onBackground?: (p: Pt, phase: 'start' | 'move') => void;
  /** pas des flèches, en unités du plan */
  keyStep?: number;
  grabRadius?: number;
}

export function useDrag<K extends string>(opts: DragOptions<K>) {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const [active, setActive] = useState<K | null>(null);
  const grab = useRef<{ id: K | null; dx: number; dy: number } | null>(null);

  const at = (e: PointerEvent<SVGSVGElement>) => {
    const o = optsRef.current;
    const svg = o.svgRef.current;
    if (!svg) return null;
    const v = pointerToViewBox(svg, e.clientX, e.clientY, o.frame.size);
    if (!v) return null;
    return { px: v.px, py: v.py, x: o.frame.ix(v.px), y: o.frame.iy(v.py) };
  };

  const finish = () => {
    const g = grab.current;
    grab.current = null;
    setActive(null);
    if (g?.id) optsRef.current.onEnd?.(g.id);
  };

  const svgProps = {
    onPointerDown: (e: PointerEvent<SVGSVGElement>) => {
      const o = optsRef.current;
      const w = at(e);
      if (!w) return;
      let best: K | null = null;
      let bestD = o.grabRadius ?? 30;
      for (const id of o.ids) {
        const p = o.pointOf(id);
        const d = Math.hypot(o.frame.tx(p.x) - w.px, o.frame.ty(p.y) - w.py);
        if (d < bestD) {
          best = id;
          bestD = d;
        }
      }
      e.currentTarget.setPointerCapture?.(e.pointerId);
      if (best !== null) {
        const p = o.pointOf(best);
        grab.current = { id: best, dx: p.x - w.x, dy: p.y - w.y };
        setActive(best);
      } else if (o.onBackground) {
        grab.current = { id: null, dx: 0, dy: 0 };
        o.onBackground({ x: w.x, y: w.y }, 'start');
      }
    },
    onPointerMove: (e: PointerEvent<SVGSVGElement>) => {
      const g = grab.current;
      if (!g) return;
      const w = at(e);
      if (!w) return;
      const o = optsRef.current;
      if (g.id !== null) o.onDrag(g.id, { x: w.x + g.dx, y: w.y + g.dy });
      else o.onBackground?.({ x: w.x, y: w.y }, 'move');
    },
    onPointerUp: finish,
    onPointerCancel: finish,
  };

  /** focus et flèches du clavier pour une poignée */
  const keyProps = (id: K) => ({
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
      const o = optsRef.current;
      const step = o.keyStep ?? 1;
      const d: Record<string, [number, number]> = {
        ArrowRight: [step, 0],
        ArrowLeft: [-step, 0],
        ArrowUp: [0, step],
        ArrowDown: [0, -step],
      };
      const move = d[e.key];
      if (!move) return;
      e.preventDefault();
      const p = o.pointOf(id);
      o.onDrag(id, { x: p.x + move[0], y: p.y + move[1] });
      o.onEnd?.(id);
    },
  });

  return { active, svgProps, keyProps };
}
